import { showToast } from './Toast.js';
import type { ComplianceSession, AuditEntry } from '../../core/types.js';

type NavigateFn = (route: { name: string; id?: string } & Record<string, unknown>) => void;

export async function renderSessionDetail(container: HTMLElement, sessionId: string, navigate: NavigateFn): Promise<void> {
  container.innerHTML = `
    <div style="display:flex;align-items:center;gap:12px;margin-bottom:24px">
      <button class="btn btn-ghost btn-sm" id="back-btn">← Back</button>
      <h1 class="page-title" style="margin:0" id="session-title">Loading…</h1>
    </div>
    <div id="session-content"><div class="empty-state"><div class="spinner"></div></div></div>
  `;

  document.getElementById('back-btn')!.addEventListener('click', () => navigate({ name: 'dashboard' }));

  try {
    const preuve = (window as unknown as { preuve: Record<string, (...args: unknown[]) => Promise<unknown>> }).preuve;
    const [session, trail] = await Promise.all([
      preuve.getSession(sessionId) as Promise<ComplianceSession>,
      preuve.getAuditTrail(sessionId) as Promise<AuditEntry[]>,
    ]);

    if (!session) {
      document.getElementById('session-content')!.innerHTML = `<div class="empty-state"><div class="empty-state-title">Session Not Found</div></div>`;
      return;
    }

    document.getElementById('session-title')!.textContent = session.documentTitle;

    const risk = inferRisk(session);
    const content = document.getElementById('session-content')!;

    content.innerHTML = `
      <div class="stats-grid" style="margin-bottom:20px">
        <div class="stat-card">
          <div class="stat-label">Status</div>
          <div style="margin-top:8px"><span class="badge badge-${session.status}" style="font-size:12px;padding:4px 12px">${session.status}</span></div>
        </div>
        <div class="stat-card">
          <div class="stat-label">Human Edit %</div>
          <div class="stat-value">${session.humanEditDelta}%</div>
          <div class="progress-bar" style="margin-top:6px">
            <div class="progress-bar-fill progress-${session.humanEditDelta < 30 ? 'low' : session.humanEditDelta < 70 ? 'medium' : 'high'}"
                 style="width:${session.humanEditDelta}%"></div>
          </div>
        </div>
        <div class="stat-card">
          <div class="stat-label">AI Provider</div>
          <div class="stat-value" style="font-size:16px">${session.provider.toUpperCase()}</div>
          <div class="stat-sub">${session.modelId}</div>
        </div>
        <div class="stat-card">
          <div class="stat-label">Risk Level</div>
          <div style="margin-top:8px"><span class="badge badge-${risk}">${risk}</span></div>
        </div>
      </div>

      <div style="display:grid;grid-template-columns:1fr 1fr;gap:20px">
        <div class="card">
          <h3 style="font-size:13px;font-weight:700;margin-bottom:16px">Document Content</h3>
          <div class="form-row">
            <label>Prompt</label>
            <div style="background:var(--bg-primary);border:1px solid var(--border);border-radius:6px;padding:10px 12px;font-size:12px;font-family:var(--font-mono);max-height:120px;overflow-y:auto;color:var(--text-secondary)">${escHtml(session.prompt)}</div>
          </div>
          <div class="form-row">
            <label>Final Output</label>
            <div style="background:var(--bg-primary);border:1px solid var(--border);border-radius:6px;padding:10px 12px;font-size:12px;font-family:var(--font-mono);max-height:200px;overflow-y:auto;color:var(--text-primary)">${escHtml(session.finalOutput)}</div>
          </div>
          <div class="form-row" style="margin-bottom:0">
            <label>Watermark Hash</label>
            <div class="hash-display">${session.watermarkHash}</div>
          </div>
        </div>

        <div class="card">
          <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:16px">
            <h3 style="font-size:13px;font-weight:700">Audit Trail</h3>
            <span style="font-size:11px;color:var(--text-muted)">${trail.length} entries</span>
          </div>
          <div class="audit-trail">
            ${trail.map((entry) => `
              <div class="audit-entry actor-${entry.actor}">
                <div class="audit-entry-dot"></div>
                <div class="audit-entry-content">
                  <div class="audit-entry-event">${formatEventType(entry.eventType)}</div>
                  <div class="audit-entry-time">${new Date(entry.timestamp).toLocaleString()}</div>
                  <div class="audit-entry-hash">${entry.currentHash}</div>
                </div>
              </div>
            `).join('')}
          </div>
        </div>
      </div>

      ${session.status === 'draft' ? `
        <div class="card" style="margin-top:20px">
          <div style="display:flex;justify-content:space-between;align-items:center">
            <div>
              <div style="font-weight:600">Ready to certify?</div>
              <div style="font-size:12px;color:var(--text-muted);margin-top:4px">This will generate a tamper-evident compliance certificate with QR code.</div>
            </div>
            <button class="btn btn-gold" id="certify-btn">◈ Certify Document</button>
          </div>
        </div>
      ` : session.complianceCertId ? `
        <div class="card" style="margin-top:20px">
          <div style="display:flex;justify-content:space-between;align-items:center">
            <div>
              <div style="font-weight:600;color:var(--success)">◈ Certificate Issued</div>
              <div style="font-size:11px;color:var(--text-muted);font-family:var(--font-mono);margin-top:4px">${session.complianceCertId}</div>
            </div>
            <div style="display:flex;gap:10px">
              <button class="btn btn-ghost" id="export-btn">↓ Export Audit JSON</button>
              <button class="btn btn-primary" id="pdf-btn">↓ Download PDF</button>
            </div>
          </div>
        </div>
      ` : ''}
    `;

    document.getElementById('certify-btn')?.addEventListener('click', async () => {
      const btn = document.getElementById('certify-btn') as HTMLButtonElement;
      btn.disabled = true;
      btn.innerHTML = '<span class="spinner"></span> Certifying…';
      try {
        await preuve.certifyDocument(sessionId);
        showToast('Document certified', 'success');
        renderSessionDetail(container, sessionId, navigate);
      } catch (err) {
        showToast(`Failed: ${(err as Error).message}`, 'error');
        btn.disabled = false;
        btn.textContent = '◈ Certify Document';
      }
    });

    document.getElementById('pdf-btn')?.addEventListener('click', async () => {
      const pdfPath = await preuve.generatePDF(sessionId) as string;
      showToast(`PDF saved: ${pdfPath}`, 'success');
    });

    document.getElementById('export-btn')?.addEventListener('click', async () => {
      const report = await preuve.exportReport(sessionId) as string;
      const blob = new Blob([report], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `preuve-audit-${sessionId.substring(0, 8)}.json`;
      a.click();
      URL.revokeObjectURL(url);
    });
  } catch (err) {
    document.getElementById('session-content')!.innerHTML = `
      <div class="empty-state">
        <div class="empty-state-title">Error Loading Session</div>
        <div class="empty-state-desc">${(err as Error).message}</div>
      </div>
    `;
  }
}

function inferRisk(session: ComplianceSession): 'low' | 'medium' | 'high' {
  if (session.humanEditDelta < 5) return 'high';
  if (session.humanEditDelta < 30) return 'medium';
  return 'low';
}

function formatEventType(type: string): string {
  return type.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

function escHtml(str: string): string {
  return str.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}
